import fs from 'fs-extra';
import path from 'path';
import yaml from 'yaml';
import { SplashConfig } from '../types/config';
import { validateConfig } from '../utils/validation';

export class ConfigLoader {
  private static readonly CONFIG_FILES = [
    'react-native-splash.yaml',
    'react-native-splash.yml',
    'package.json',
  ];

  static async loadConfig(configPath?: string, flavor?: string): Promise<SplashConfig> {
    let config: SplashConfig;

    if (configPath) {
      config = await this.loadFromFile(configPath);
    } else if (flavor) {
      config = await this.loadFlavorConfig(flavor);
    } else {
      config = await this.loadDefaultConfig();
    }

    validateConfig(config);
    return config;
  }

  private static async loadFromFile(filePath: string): Promise<SplashConfig> {
    if (!(await fs.pathExists(filePath))) {
      throw new Error(`Config file not found: ${filePath}`);
    }

    const ext = path.extname(filePath).toLowerCase();
    const content = await fs.readFile(filePath, 'utf-8');

    if (ext === '.json') {
      const data = JSON.parse(content);
      return data.reactNativeSplash || data['react-native-splash'] || data;
    } else if (ext === '.yaml' || ext === '.yml') {
      const data = yaml.parse(content);
      return data.reactNativeSplash || data['react-native-splash'] || data;
    } else {
      throw new Error(`Unsupported config file format: ${ext}`);
    }
  }

  private static async loadFlavorConfig(flavor: string): Promise<SplashConfig> {
    const flavorFile = `react-native-splash-${flavor}.yaml`;
    if (await fs.pathExists(flavorFile)) {
      return this.loadFromFile(flavorFile);
    }

    const altFlavorFile = `react-native-splash-${flavor}.yml`;
    if (await fs.pathExists(altFlavorFile)) {
      return this.loadFromFile(altFlavorFile);
    }

    throw new Error(`Flavor config file not found for flavor: ${flavor}`);
  }

  private static async loadDefaultConfig(): Promise<SplashConfig> {
    for (const configFile of this.CONFIG_FILES) {
      if (await fs.pathExists(configFile)) {
        try {
          return await this.loadFromFile(configFile);
        } catch (error) {
          if (configFile === 'package.json') {
            // For package.json, it might not have splash config, continue to next
            continue;
          }
          throw error;
        }
      }
    }

    throw new Error(
      'No configuration file found. Please create one of: ' + this.CONFIG_FILES.join(', ')
    );
  }

  static async findFlavorConfigs(): Promise<string[]> {
    const files = await fs.readdir(process.cwd());
    const flavorFiles = files.filter(file => /^react-native-splash-[^-]+\.(yaml|yml)$/.test(file));

    return flavorFiles
      .map(file => {
        const match = file.match(/^react-native-splash-([^-]+)\.(yaml|yml)$/);
        return match ? match[1] : '';
      })
      .filter(Boolean);
  }
}

export const loadConfig = ConfigLoader.loadConfig.bind(ConfigLoader);
